import {SmallLoader} from '@kit';

import React, {useMemo} from 'react';

import {Feature, Image, Specie, useSpecieQuery, ImageTarget} from '../../../generated/types';
import {buildUrl, useRoute} from '../../../lib/routes';
import {
	getApolloError,
	wordByCount,
	humanEnumeration,
	toUpperFirst as ToUpperFirst,
	getSourceText,
} from '../../../components/utils';
import {PageTools} from '../../../components/page-tools';
import {EditHoc} from '../../../components/edit-hoc';
import {FeatureSeo, FeatureView, groupFeatures} from '../../../components/feature-view';
import {OtherTitles} from '../../../components/other-titles';
import {NotFoundSeo, isNotFoundError, useNotFound} from '../../../lib/seo';
import {SpecieSeo} from '../seo';
import {NotFound} from '../../../components/not-found';
import {CreatureTypes, sizesToString} from '../../../components/enums';
import {Markdown} from '../../../components/utils/md';
import {Gallery} from '../../../components/gallery';
import {CommentTargetType, Comments} from '../../../components/comments';

import dashboardCss from '@kit/sidebar/dashboard.module.css';
import pageCss from '../../../components/common.module.css';
import markdownCss from '../../../components/utils/md/markdown.module.css';

type Props = {
	specieId: string;
	title: string;
};

export const SpecieView: React.FC<Props> = ({specieId, title}) => {
	const {data, loading, error} = useSpecieQuery({variables: {id: specieId}});
	const specie = data?.specie;
	const notFound = !loading && !specie && isNotFoundError(error);
	useNotFound(notFound);
	const {routeName, query} = useRoute();
	// /specie/:id/:featureId рендерит ту же карточку, поэтому каноничный адрес — сам вид
	const openFeature =
		routeName === 'specieFeature' ? specie?.features?.find((feature) => feature.id === query.featureId) : undefined;

	const features = useMemo(() => {
		if (specie) return groupFeatures(specie.features as Feature[]);
		return [];
	}, [specie]);
	const baseFeatures = useMemo(() => features.filter((f) => !f.parentId), [features]);

	return (
		<article className={dashboardCss.readingWrapper}>
			{specie &&
				(openFeature ? (
					<FeatureSeo
						feature={openFeature}
						section="species"
						owner={`особенность вида ${specie.title}`}
						lead={`Особенность вида ${specie.title}`}
						canonicalPath={buildUrl('specie', {id: specie.id})}
						crumbs={[
							{name: specie.title, path: buildUrl('specie', {id: specie.id})},
							{
								name: openFeature.title,
								path: buildUrl('specieFeature', {id: specie.id, featureId: openFeature.id}),
							},
						]}
					/>
				) : (
					<SpecieSeo specie={specie as Specie} />
				))}
			{notFound && <NotFoundSeo />}
			<PageTools />
			<h1 className={dashboardCss.mainTitle}>{title}</h1>
			<div className={dashboardCss.page}>
				{loading && (
					<div className={pageCss.fullSizeBlock}>
						<SmallLoader />
					</div>
				)}
				{notFound ? (
					<NotFound />
				) : (
					error && <div className={pageCss.fullSizeBlock}>{getApolloError(error).message}</div>
				)}
				{specie && (
					<div>
						<div>
							<Markdown
								text={`
						[indent]
							*Тип существа:* ${ToUpperFirst(CreatureTypes[specie?.type])}
							*Размер:* ${humanEnumeration(
								(specie?.sizes || []).map((size) => {
									return `${sizesToString(size.category)} (рост от ${size.tallFrom} до ${
										size.tallTo
									} футов)`;
								}),
								'или',
							)}
						*Скорость:* ${wordByCount(specie?.speed || 0, ['фут', 'фута', 'футов'], true)}
						[/indent]
						`}
							/>
							<EditHoc
								load={() => import('../edit-buttons').then((m) => m.EditSpecie)}
								editProps={{id: specie.id}}
							>
								{specie.featuresDescription || 'Базовое описание особенностей вида.'}
							</EditHoc>

							{baseFeatures.map((feature) => (
								<FeatureView
									key={feature.id}
									feature={feature as Feature}
									parentObject={{type: 'specie', parentId: specie.id}}
									h={5}
								/>
							))}

							<div className={markdownCss.margin}>
								<div className={markdownCss.indent}>
									<OtherTitles originalTitle={specie.originalTitle} />
									<p>
										<b>Источник: </b>
										{getSourceText(specie.source as any)}.
									</p>
								</div>
							</div>

							<h2>Описание</h2>
							<EditHoc
								load={() => import('../edit-buttons').then((m) => m.EditDescription)}
								editProps={{id: specie.id}}
							>
								<Markdown text={specie.description || ''} />
							</EditHoc>

							<Gallery images={(specie.images || []) as Image[]} target={{type: ImageTarget.Specie, id: specieId}} />
						</div>
					</div>
				)}
				{specie && <Comments targetType={CommentTargetType.Specie} targetId={specieId} />}
			</div>
		</article>
	);
};
